import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiService from '../../services/api';
import { isAdminOrOwner, isManagerOrAbove } from '../../lib/roles';
import { subscribeToExpensesUpdates } from '../../services/socket';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { Plus, Edit, Trash2, X, Wallet, Pencil, Download, Loader2 } from 'lucide-react';
import { exportExpensesToExcel } from '../../utils/excelExport';
import { formatDate, formatCurrency, toCalendarDateString, todayCalendarDateString } from '../../utils/format';
import { getTokens } from '../../utils/tokens';
import { getAdminSelectCSS, getAdminFormSelectCSS } from '../../utils/adminSelect';
import {
  PageHeader,
  StatStrip,
  PrimaryBtn,
  GhostBtn,
  Toolbar,
  FilterChip,
  DataTable,
  Pagination,
  MonoMoney,
  StatusPill,
  AvatarInitials,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';

const Expenses = () => {
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const tk = getTokens(mode);

  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [exporting, setExporting] = useState(false);

  const [filters, setFilters] = useState({
    category_id: 'all',
    start_date: '',
    end_date: '',
    search: '',
  });

  const canManage = isManagerOrAbove(user?.role);
  const canEditDelete = isAdminOrOwner(user?.role);

// ── Style helpers ────────────────────────────────────────────────────────────
const filterSelect = getAdminSelectCSS(tk, 'exp-filter');
const formSelect = getAdminFormSelectCSS(tk, 'exp-form');

const dateInput = {
    height: 34, padding: '0 10px', background: tk.surface,
    border: `1px solid ${tk.border}`, borderRadius: 8,
    color: tk.text, fontFamily: 'inherit', fontSize: 12, outline: 'none',
  };

  const inputStyle = {
    width: '100%', height: 40, background: tk.sunken,
    border: `1px solid ${tk.border}`, borderRadius: 8,
    padding: '0 12px', color: tk.text, fontFamily: 'inherit',
    fontSize: 13, outline: 'none',
  };

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: tk.textMuted,
    textTransform: 'uppercase', letterSpacing: 0.4,
    marginBottom: 6, display: 'block',
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };

  const modalBox = {
    background: tk.surface, border: `1px solid ${tk.border}`,
    borderRadius: 14, padding: 24, maxWidth: 520,
    width: '100%', margin: '0 16px', maxHeight: '90vh', overflowY: 'auto',
  };

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { loadExpenses(); }, [filters]);
  useEffect(() => {
    const unsubscribe = subscribeToExpensesUpdates(() => {
      loadCategories();
      loadExpenses();
    });
    return unsubscribe;
  }, [filters]);

  const loadCategories = async () => {
    try {
      const res = await apiService.getExpenseCategories();
      setCategories(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to load expense categories', err);
    }
  };

  const loadExpenses = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        search: filters.search?.trim() || undefined,
      };
      if (filters.category_id && filters.category_id !== 'all') {
        params.category_id = filters.category_id;
      }
      const res = await apiService.getExpenses(params);
      setExpenses(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('alerts.failedToLoadExpenses'));
      setExpenses([]);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil((expenses || []).length / itemsPerPage);
  const currentExpenses = React.useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return (expenses || []).slice(indexOfFirstItem, indexOfLastItem);
  }, [expenses, currentPage, itemsPerPage]);

  useEffect(() => { setCurrentPage(1); }, [filters]);

  // ── Derived stats ─────────────────────────────────────────────────────────────
  const now = new Date();
  const thisMonthExpenses = expenses.filter(e => {
    const d = new Date(e.bought_at || e.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const thisMonthTotal = thisMonthExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);

  const totalAmount = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  // ── Category color index ──────────────────────────────────────────────────────
  const catColorIndex = {};
  (categories || []).forEach((c, i) => {
    catColorIndex[c.id] = i;
    catColorIndex[c.name] = i;
  });

  const hasActiveFilters =
    filters.category_id !== 'all' ||
    filters.search?.trim() !== '' ||
    !!filters.start_date ||
    !!filters.end_date;

  const clearFilters = () => {
    setFilters({
      category_id: 'all',
      start_date: '',
      end_date: '',
      search: '',
    });
  };

  // ── Filter options ────────────────────────────────────────────────────────────
  const categoryOptions = [
    { value: 'all', label: t('common.all') },
    ...categories.map((c) => ({ value: String(c.id), label: c.name })),
  ];

  // ── Table columns ─────────────────────────────────────────────────────────────
  const columns = [
    { key: 'bought_at', label: t('expenses.date') },
    { key: 'category_name', label: t('expenses.category') },
    { key: 'name', label: t('expenses.expenseName') },
    { key: 'amount', label: t('expenses.amount') },
    ...(canEditDelete ? [{ key: 'actions', label: t('common.actions') }] : []),
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case 'bought_at':
        return (
          <span style={{ color: tk.textSecondary, fontSize: 12 }}>
            {row.bought_at ? formatDate(row.bought_at) : '—'}
          </span>
        );
      case 'category_name': {
        const catName = row.category_name || '—';
        const idx = catColorIndex[row.category_id || catName] || 0;
        const color = tk.cat[idx % tk.cat.length];
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
            <span style={{ color: tk.textSecondary, fontSize: 12 }}>{catName}</span>
          </span>
        );
      }
      case 'name':
        return <span style={{ color: tk.text, fontWeight: 500 }}>{row.name}</span>;
      case 'amount':
        return <MonoMoney amount={row.amount} tk={tk} size={12.5} weight={600} />;
      case 'actions':
        return (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            <button
              onClick={() => setEditingExpense(row)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6,
                background: tk.accentSoft, border: 'none',
                color: tk.accentText, cursor: 'pointer',
              }}
              title={t('common.edit')}
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => setDeletingExpense(row)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6,
                background: tk.dangerSoft, border: 'none',
                color: tk.dangerText, cursor: 'pointer',
              }}
              title={t('common.delete')}
            >
              <Trash2 size={13} />
            </button>
          </span>
        );
      default:
        return null;
    }
  };

  // ── ExpenseModal ──────────────────────────────────────────────────────────────
  const ExpenseModal = ({ expense, onClose, onSave }) => {
    const [formData, setFormData] = useState(
      expense
        ? {
          name: expense.name,
          amount: expense.amount,
          category_id: expense.category_id,
          bought_at: toCalendarDateString(expense.bought_at) || todayCalendarDateString(),
        }
        : {
          name: '',
          amount: '',
          category_id: '',
          bought_at: todayCalendarDateString(),
        }
    );
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSaving(true);
      try {
        const amount = parseFloat(formData.amount);
        if (isNaN(amount) || amount <= 0) throw new Error(t('expenses.invalidAmount'));
        if (!formData.name?.trim()) throw new Error(t('expenses.nameRequired'));
        if (formData.name.trim().length > 50) throw new Error(t('alerts.expenseNameMaxLength'));
        if (!formData.category_id) throw new Error(t('expenses.categoryRequired'));

        const payload = {
          name: formData.name.trim(),
          amount,
          category_id: formData.category_id,
          bought_at: formData.bought_at,
        };
        if (expense) {
          await apiService.updateExpense(expense.id, payload);
        } else {
          await apiService.createExpense(payload);
        }
        onSave();
      } catch (err) {
        showToast(err.body?.error || err.message || t('alerts.failedToSaveExpense'), "error");
      }
      setSaving(false);
    };

    return (
      <div style={overlay}>
        <div style={modalBox}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: tk.text, margin: 0 }}>
              {expense ? t('expenses.editExpense') : t('expenses.addExpense')}
            </h3>
            <button
              onClick={onClose}
              style={{
                width: 28, height: 28, background: tk.surfaceAlt,
                border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: tk.textMuted,
              }}
            >
              <X size={13} />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>{t('expenses.expenseName')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  maxLength={50}
                  style={inputStyle}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={labelStyle}>{t('expenses.amount')} ({t('currency.egp')})</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="9999999.99"
                    value={formData.amount}
                    onChange={(e) => {
                      if (e.target.value !== '' && parseFloat(e.target.value) > 9999999.99) return;
                      setFormData({ ...formData, amount: e.target.value });
                    }}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t('expenses.category')}</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className={formSelect.className}
                    required
                  >
                    <option value="">{t('expenses.selectCategory')}</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>{t('expenses.date')}</label>
                <input
                  type="date"
                  value={formData.bought_at}
                  onChange={(e) => setFormData({ ...formData, bought_at: e.target.value })}
                  style={{ ...inputStyle, height: 40, padding: '0 12px' }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: `1px solid ${tk.border}`,
                  background: 'transparent', color: tk.textSecondary,
                  fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: tk.accent, color: '#fff',
                  fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? t('common.loading') : expense ? t('common.update') : t('expenses.addExpense')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ── CategoryManagementModal ───────────────────────────────────────────────────
  const CategoryManagementModal = ({ onClose }) => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingCategoryLocal, setEditingCategoryLocal] = useState(null);
    const [formData, setFormData] = useState({ name: '' });
    const [saving, setSaving] = useState(false);

    const handleSaveCategory = async (e) => {
      e.preventDefault();
      if (!formData.name?.trim()) return;
      if (formData.name.trim().length > 30) {
        showToast(t('alerts.expenseCategoryNameMaxLength'), "warning");
        return;
      }
      const nameTrimmed = formData.name.trim();
      const duplicate = categories.find(
        (c) => c.name.toLowerCase() === nameTrimmed.toLowerCase() && c.id !== editingCategoryLocal?.id
      );
      if (duplicate) {
        showToast(t('expenses.categoryNameUnique'), "warning");
        return;
      }
      setSaving(true);
      try {
        if (editingCategoryLocal) {
          await apiService.updateExpenseCategory(editingCategoryLocal.id, { name: formData.name.trim() });
        } else {
          await apiService.createExpenseCategory({ name: formData.name.trim() });
        }
        await loadCategories();
        setShowAddForm(false);
        setEditingCategoryLocal(null);
        setFormData({ name: '' });
      } catch (err) {
        const apiError = err.body?.error || err.message;
        const showError =
          apiError && apiError.includes('already exists')
            ? t('expenses.categoryNameUnique')
            : apiError || t('alerts.failedToSaveExpenseCategory');
        showToast(showError, "error");
      }
      setSaving(false);
    };

    return (
      <div style={overlay}>
        <div style={{
          background: tk.surface, border: `1px solid ${tk.border}`,
          borderRadius: 14, width: '100%', maxWidth: 480,
          margin: '0 16px', display: 'flex', flexDirection: 'column',
          maxHeight: '80vh', overflow: 'hidden',
          boxShadow: tk.shadowLg,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${tk.border}`,
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {t('expenses.manageCategories')}
            </span>
            <button
              onClick={onClose}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6,
                background: 'transparent', border: `1px solid ${tk.border}`,
                color: tk.textSecondary, cursor: 'pointer',
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
            {(showAddForm || editingCategoryLocal) && (
              <form onSubmit={handleSaveCategory} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${tk.border}` }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: tk.text, marginBottom: 10 }}>
                  {editingCategoryLocal ? t('expenses.editCategory') : t('expenses.addCategory')}
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ name: e.target.value })}
                    placeholder={t('expenses.categoryName')}
                    maxLength={30}
                    style={{ ...inputStyle, flex: 1, height: 38 }}
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      height: 38, padding: '0 16px', borderRadius: 8,
                      background: tk.accent, border: 'none',
                      color: '#fff', cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {saving ? t('common.loading') : t('common.save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingCategoryLocal(null);
                      setFormData({ name: '' });
                    }}
                    style={{
                      height: 38, padding: '0 12px', borderRadius: 8,
                      background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
                      color: tk.textSecondary, cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12.5,
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </form>
            )}

            {!showAddForm && !editingCategoryLocal && (
              <button
                onClick={() => { setShowAddForm(true); setFormData({ name: '' }); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  height: 34, padding: '0 14px', borderRadius: 8, marginBottom: 12,
                  background: tk.accentSoft, border: 'none',
                  color: tk.accentText, cursor: 'pointer',
                  fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                }}
              >
                <Plus size={13} />
                {t('expenses.addCategory')}
              </button>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {categories.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 13 }}>
                  {t('expenses.noCategories')}
                </p>
              ) : (
                categories.map((cat, i) => (
                  <div
                    key={cat.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 14px', borderRadius: 8,
                      border: `1px solid ${tk.border}`, background: tk.surfaceAlt,
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: 2,
                        background: tk.cat[i % tk.cat.length], flexShrink: 0,
                      }} />
                      <span style={{ fontSize: 13, fontWeight: 500, color: tk.text }}>{cat.name}</span>
                    </span>
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button
                        onClick={() => { setEditingCategoryLocal(cat); setFormData({ name: cat.name }); }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6,
                          background: tk.accentSoft, border: 'none',
                          color: tk.accentText, cursor: 'pointer',
                        }}
                      >
                        <Edit size={13} />
                      </button>
                      <button
                        onClick={() => setDeletingCategory(cat)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 28, height: 28, borderRadius: 6,
                          background: tk.dangerSoft, border: 'none',
                          color: tk.dangerText, cursor: 'pointer',
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── DeleteConfirmModal ────────────────────────────────────────────────────────
  const DeleteConfirmModal = ({ title, message, onClose, onConfirm }) => (
    <div style={{ ...overlay, zIndex: 60 }}>
      <div style={{
        background: tk.surface, border: `1px solid ${tk.border}`,
        borderRadius: 14, width: '100%', maxWidth: 400,
        margin: '0 16px', padding: '24px',
        boxShadow: tk.shadowLg,
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 10 }}>{title}</div>
        <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 22, lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, height: 38, borderRadius: 8,
              background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
              color: tk.textSecondary, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => { onConfirm(); onClose(); }}
            style={{
              flex: 1, height: 38, borderRadius: 8,
              background: tk.danger, border: 'none',
              color: '#fff', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            }}
          >
            {t('common.delete')}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────────
  const wrapperStyle = {
    background: tk.bg, color: tk.text, padding: 20,
    minHeight: '100%', fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
    overflowY: 'auto',
  };
  const toolbarFilterLabelStyle = {
    fontSize: 10.5,
    color: tk.textMuted,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  };

  return (
    <div style={wrapperStyle} className={`theme-${mode}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <style>{filterSelect.css}{formSelect.css}</style>
      <PageHeader
        tk={tk}
        title={t('expenses.title')}
        subtitle={t('expenses.subtitle') || 'Track operating costs'}
        actions={
          <>
            <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={async () => {
              if (!expenses.length) return;
              setExporting(true);
              try {
                const labels = t('expenses.excelExport');
                const L = typeof labels === 'object' && labels !== null ? labels : {};
                const today = todayCalendarDateString();
                await exportExpensesToExcel(expenses, `expenses-${today}.xlsx`, L);
              } catch (err) {
                console.error('Export failed', err);
              } finally {
                setExporting(false);
              }
            }}>
              {exporting ? t('monthlyClosing.downloading') : t('expenses.downloadExcel')}
            </GhostBtn>
            {canManage && (
              <GhostBtn tk={tk} onClick={() => setShowCategoryModal(true)} icon={Wallet}>
                {t('expenses.manageCategories')}
              </GhostBtn>
            )}
            {canManage && (
              <PrimaryBtn tk={tk} onClick={() => setShowAddModal(true)} icon={Plus}>
                {t('expenses.addExpense')}
              </PrimaryBtn>
            )}
          </>
        }
      />

      {loading ? (
        <StatStripSkeleton tk={tk} count={3} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: t('expenses.totalExpenses') || 'Total', value: totalAmount, money: true },
            { label: t('expenses.thisMonth') || 'This month', value: thisMonthTotal, money: true },
            { label: t('expenses.expenseCount') || 'Entries', value: String(expenses.length) },
          ]}
        />
      )}

      {error && (
        <div style={{
          background: tk.dangerSoft, border: `1px solid ${tk.danger}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 12,
          color: tk.dangerText, fontSize: 12.5,
        }}>
          {t('common.error')}: {error}
        </div>
      )}

      <Toolbar
        tk={tk}
        searchPlaceholder={t('expenses.searchPlaceholder') || 'Search expenses...'}
        searchValue={filters.search}
        onSearchChange={(v) => setFilters((prev) => ({ ...prev, search: v }))}
        filters={
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={toolbarFilterLabelStyle}>{t('common.from')}</span>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))}
                style={dateInput}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={toolbarFilterLabelStyle}>{t('common.to')}</span>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))}
                style={dateInput}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 155 }}>
              <span style={toolbarFilterLabelStyle}>{t('inventory.category')}</span>
              <select
                value={filters.category_id}
                onChange={(e) => setFilters((prev) => ({ ...prev, category_id: e.target.value }))}
                style={{ minWidth: 155 }}
                className={filterSelect.className(filters.category_id !== 'all')}
                data-active={filterSelect.dataActive(filters.category_id !== 'all')}
              >
                <option value="all">{language === 'ar' ? 'كل الفئات' : 'All categories'}</option>
                {(categories || []).map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  alignSelf: 'flex-end',
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  height: 34, padding: '0 10px', borderRadius: 8,
                  background: tk.dangerSoft, border: 'none',
                  color: tk.dangerText, fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <X size={12} />
                {t('common.clear')}
              </button>
            )}
          </>
        }
      />

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[0.7, 1.5, 1.5, 3, 1.5, 1]} />
      ) : (
        <DataTable
          tk={tk}
          columns={columns}
          rows={currentExpenses}
          renderCell={renderCell}
          emptyMessage={
            filters.search || filters.category_id !== 'all'
              ? t('expenses.tryDifferentFilters')
              : t('expenses.addFirstExpense')
          }
        />
      )}

      <Pagination
        tk={tk}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* Modals */}
      {showAddModal && (
        <ExpenseModal
          onClose={() => setShowAddModal(false)}
          onSave={() => { setShowAddModal(false); loadExpenses(); }}
        />
      )}
      {editingExpense && (
        <ExpenseModal
          expense={editingExpense}
          onClose={() => setEditingExpense(null)}
          onSave={() => { setEditingExpense(null); loadExpenses(); }}
        />
      )}
      {showCategoryModal && (
        <CategoryManagementModal onClose={() => setShowCategoryModal(false)} />
      )}
      {deletingExpense && (
        <DeleteConfirmModal
          title={t('expenses.deleteExpense')}
          message={`${t('common.delete')} "${deletingExpense?.name}"${t('common.questionMark')} ${t('expenses.deleteExpenseConfirm')}`}
          onClose={() => setDeletingExpense(null)}
          onConfirm={async () => {
            try {
              await apiService.deleteExpense(deletingExpense.id);
              loadExpenses();
            } catch (e) {
              showToast(e.body?.error || e.message || t('alerts.deleteFailed'), "warning");
            }
          }}
        />
      )}
      {deletingCategory && (
        <DeleteConfirmModal
          title={t('expenses.deleteCategory')}
          message={t('expenses.deleteCategoryConfirm').replace('{name}', deletingCategory.name)}
          onClose={() => setDeletingCategory(null)}
          onConfirm={async () => {
            try {
              await apiService.deleteExpenseCategory(deletingCategory.id);
              await loadCategories();
              loadExpenses();
            } catch (err) {
              showToast(err.body?.error || err.message || t('alerts.failedToDeleteExpenseCategory'), "error");
            }
          }}
        />
      )}
    </div>
  );
};

export default Expenses;
